import {RefSelect, Options} from '@fl/cmsch-fe-library';
import {RefObject, useCallback} from 'react';
import {opt, Opt, testRe, uncurryTuple} from 'ts-opt';

import {ApcStableDate} from 'api/gen/ApcStableDate';
import {CorrectionType} from 'api/gen/CorrectionType';
import {Date} from 'api/gen/Date';
import {EarTag, earTagRegexGen} from 'api/gen/EarTag';
import {StableCode} from 'api/gen/StableCode';

import {
    genProtocolDateOptionsBasedOnStable,
    genProtocolStableOptionsBasedOnDate,
    genProtocolStableOptionsBasedOnStable,
    genNonProtocolDateOptionsBasedOnStable,
    getDateOptions,
} from '../utils/get-apc-options';
import {isApcDateListWithStables, isApcStableListWithDates} from '../utils/is-apc-stable-date';
import {protocolExists} from '../utils/protocol-exists';

/** Public callback contract returned by {@link useDateAndStableChange}. */
interface DateAndStableChangeCallbacks {
    /** Handles stable-number changes. */
    onStableNumberChange(stableName: Opt<StableCode>): void;
    /** Handles proficiency-test date changes. */
    onProficiencyTestDateChange(val: Opt<Date>): void;
}

/** Dependencies required to coordinate stable/date field changes in the APC form. */
interface Props {
    /** Stable/date relations resolved for the current APC form context. */
    stableDateRelations: Opt<ApcStableDate>;
    /** Currently selected correction type. */
    correctionType: CorrectionType | undefined;
    /** Updated ear-tag input ref used for focus orchestration. */
    earTagChangeRef: RefObject<HTMLInputElement>;
    /** Milk-kg input ref used for focus orchestration. */
    milkKgRef: RefObject<HTMLInputElement>;
    /** Stable-number select ref used for focus orchestration. */
    stableNumberRef: RefSelect;
    /** Proficiency-test date select ref used for focus orchestration. */
    proficiencyTestDateRef: RefSelect;
    /** Currently selected stable number. */
    stableNumber: StableCode | null;
    /** Current ear-tag value used for protocol lookups. */
    earTag: EarTag | null;
    /** Whether the active correction type is `UPDATE`. */
    isUpdateCorrection: boolean;
    /** Submit button ref used for focus orchestration. */
    submitRef: RefObject<HTMLButtonElement>;
    /** Loads protocol details for the current ear tag and date. */
    getAnalysisProtocol(earTag: EarTag, proficiencyTestDate: Date): void;
    /** Loads stable/date relations for the selected stable and date. */
    getStableDateRelation(stableCode: StableCode, proficiencyTestDate: Date): void;
    /** Replaces proficiency-test date options in local form state. */
    setProficiencyTestDateOptions(options: Options<string>): void;
    /** Writes a single APC form field value. */
    change(field: string, value: unknown): void;
    /** Replaces stable options in local form state. */
    setStableOptions(options: Options<string>): void;
}

/**
 * Builds the APC stable/date change handlers that keep dependent fields in sync.
 *
 * @param props Form state, refs, async loaders, and setters used by the change handlers.
 * @returns Stable-number and proficiency-test-date handlers for the APC form.
 *
 * @example
 * const {onStableNumberChange, onProficiencyTestDateChange} = useDateAndStableChange({
 *   stableDateRelations,
 *   correctionType,
 *   earTagChangeRef,
 *   milkKgRef,
 *   stableNumberRef,
 *   proficiencyTestDateRef,
 *   stableNumber,
 *   earTag,
 *   isUpdateCorrection,
 *   submitRef,
 *   getAnalysisProtocol,
 *   getStableDateRelation,
 *   setProficiencyTestDateOptions,
 *   change,
 *   setStableOptions,
 * });
 */
// eslint-disable-next-line max-lines-per-function
export const useDateAndStableChange = ({
    stableDateRelations,
    earTag,
    stableNumber,
    getStableDateRelation,
    getAnalysisProtocol,
    setProficiencyTestDateOptions,
    change,
    correctionType,
    milkKgRef,
    proficiencyTestDateRef,
    submitRef,
    stableNumberRef,
    earTagChangeRef,
    setStableOptions,
    isUpdateCorrection,
}: Props): DateAndStableChangeCallbacks => {
    const clearAdditionalDataInProtocol = useCallback((
        changedField: 'proficiencyTestDate' | 'stableNumber',
        value: string | null,
    ) => {
        change('milkingTripleAttribute', null);
        change('milkKg', null);
        change('fatPercentage', null);
        change('proteinPercentage', null);
        change('lactosePercentage', null);
        change('somaticCells', null);
        change('urea', null);
        change(changedField, value);
    }, [change]);

    // eslint-disable-next-line max-lines-per-function
    const onProficiencyTestDateChange = useCallback((dateValue: Opt<Date>) => {
        clearAdditionalDataInProtocol('proficiencyTestDate', dateValue.orNull());
        if (dateValue.isFull) {
            if (!protocolExists(stableDateRelations) && stableNumber) {
            // protocol does not exist, value is not empty, implies correctionType === 'UPDATE'
                getStableDateRelation(stableNumber, dateValue.orElse(''));
                setTimeout(() => milkKgRef.current?.focus());
            } else {
            // protocol exists
                if (!isUpdateCorrection) {
                    stableDateRelations.narrow(isApcDateListWithStables)
                        .zip(dateValue)
                        .map(uncurryTuple(genProtocolStableOptionsBasedOnDate))
                        .onSome(options => {
                            setStableOptions(options);
                            if (options.length === 1) {
                                change('stableNumber', options[0]?.value);
                                if (correctionType === 'EAR_TAG_UPDATE') {
                                    setTimeout(() => earTagChangeRef.current?.focus());
                                }
                            } else {
                                setTimeout(() => stableNumberRef.current?.focus());
                            }
                        });
                    if (earTag && testRe(earTagRegexGen())(earTag)) {
                        getAnalysisProtocol(earTag, dateValue.orElse(''));
                    }
                } else {
                    // protocol exists and correctionType === 'UPDATE'
                    if (stableNumber) {
                        stableDateRelations.narrow(isApcDateListWithStables).onSome(relations => {
                            const equalDateDataInProtocol = relations.dateData?.filter(
                                date => date.proficiencyTestDate === dateValue.orElse(''),
                            );
                            const isStableInProtocolDateData = equalDateDataInProtocol?.some(data => data.stables?.some(
                                stable => stable.code === stableNumber),
                            );
                            if (isStableInProtocolDateData) {
                                getAnalysisProtocol(earTag ?? '', dateValue.orElse(''));
                            } else {
                                getStableDateRelation(stableNumber, dateValue.orElse(''));
                            }
                        });
                    } else {
                        getAnalysisProtocol(earTag ?? '', dateValue.orElse(''));
                    }
                    stableDateRelations.narrow(isApcDateListWithStables)
                        .zip(dateValue)
                        .map(uncurryTuple(genProtocolStableOptionsBasedOnDate))
                        .filter(xs => xs.length === 1)
                        .onSome(options => change('stableNumber', options[0]?.value));
                    setTimeout(() => milkKgRef.current?.focus());
                }
            }
        } else {
            if (isUpdateCorrection && stableNumber) {
                stableDateRelations.narrow(isApcDateListWithStables)
                    .zip(opt(stableNumber))
                    .map(uncurryTuple(genProtocolDateOptionsBasedOnStable))
                    .onSome(setProficiencyTestDateOptions);
            }
        }
    }, [
        earTag,
        getStableDateRelation,
        isUpdateCorrection,
        milkKgRef,
        change,
        correctionType,
        earTagChangeRef,
        stableNumberRef,
        getAnalysisProtocol,
        setStableOptions,
        stableDateRelations,
        stableNumber,
        clearAdditionalDataInProtocol,
        setProficiencyTestDateOptions,
    ]);
    // eslint-disable-next-line max-lines-per-function
    const onStableNumberChange = useCallback((stableName: Opt<StableCode>) => {
        if (isUpdateCorrection) {
            clearAdditionalDataInProtocol('stableNumber', stableName.orNull());
            change('proficiencyTestDate', null);
            stableDateRelations.narrow(isApcStableListWithDates)
                .zip(stableName)
                .map(uncurryTuple(genNonProtocolDateOptionsBasedOnStable))
                .onSome(setProficiencyTestDateOptions);
            if (stableName.isFull) setTimeout(() => proficiencyTestDateRef.current?.focus());
        }
        if (protocolExists(stableDateRelations)) {
            if (!stableName.isEmpty) {
                if (!isUpdateCorrection) {
                    setTimeout(() => earTagChangeRef.current?.focus());
                } else {
                    // protocol exists, correctionType === 'UPDATE', value exists
                    stableDateRelations.narrow(isApcDateListWithStables)
                        .zip(stableName)
                        .map(uncurryTuple(genProtocolDateOptionsBasedOnStable))
                        .onSome(setProficiencyTestDateOptions);
                    setTimeout(() => proficiencyTestDateRef.current?.focus());
                }
            } else if (isUpdateCorrection) {
                // stableName is empty and onStableNumberChange occured => field was cleared,
                // correctionType === 'UPDATE' and protocol exists =>
                // clear date options and value and set stable options
                stableDateRelations.narrow(isApcDateListWithStables)
                    .prop('dateData')
                    .map(getDateOptions)
                    .onSome(setProficiencyTestDateOptions);
                stableDateRelations.narrow(isApcDateListWithStables)
                    .map(genProtocolStableOptionsBasedOnStable)
                    .onSome(setStableOptions);
            }
            if (correctionType === 'DISCARD') setTimeout(() => submitRef.current?.focus());
        }
    }, [
        correctionType,
        submitRef,
        change,
        isUpdateCorrection,
        earTagChangeRef,
        proficiencyTestDateRef,
        setProficiencyTestDateOptions,
        setStableOptions,
        stableDateRelations,
        clearAdditionalDataInProtocol,
    ]);

    return {onStableNumberChange, onProficiencyTestDateChange};
};
